import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MembershipTierModal = ({ isOpen, onClose, onSelectTier }) => {
  if (!isOpen) return null;

  const membershipTiers = [
    {
      id: 'free',
      name: 'Collector',
      price: 'Free',
      period: 'Forever',
      description: 'Perfect for getting started with your collection tracking',
      features: [
        'Track up to 50 items',
        'Basic market insights',
        'Mobile app access',
        'Community access',
        'Email support'
      ],
      limitations: [
        'Limited analytics',
        'Basic reporting'
      ],
      popular: false,
      color: 'from-gray-500 to-gray-600'
    },
    {
      id: 'premium',
      name: 'Curator',
      price: '$19',
      period: 'per month',
      description: 'Advanced tools for serious collectors and investors',
      features: [
        'Unlimited item tracking',
        'Advanced market analytics',
        'Portfolio performance reports',
        'Price alerts & notifications',
        'Export capabilities',
        'Priority support',
        'API access'
      ],
      limitations: [],
      popular: true,
      color: 'from-primary to-warning'
    },
    {
      id: 'professional',
      name: 'Connoisseur',
      price: '$49',
      period: 'per month',
      description: 'Professional-grade platform for dealers and institutions',
      features: [
        'Everything in Curator',
        'Multi-user accounts',
        'Custom reporting',
        'White-label options',
        'Dedicated account manager',
        'Custom integrations',
        'Advanced security features'
      ],
      limitations: [],
      popular: false,
      color: 'from-purple-500 to-purple-600'
    }
  ];

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl shadow-premium-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Choose Your Membership</h2>
              <p className="text-muted-foreground mt-1">
                Select the plan that best fits your collecting needs
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted/50 rounded-lg transition-colors duration-200"
            >
              <Icon name="X" size={24} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {membershipTiers?.map((tier) => (
              <div
                key={tier?.id}
                className={`relative bg-surface border-2 rounded-xl p-6 transition-all duration-300 premium-hover ${
                  tier?.popular 
                    ? 'border-primary shadow-gold' 
                    : 'border-border hover:border-primary/50'
                }`}
              >
                {tier?.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-br ${tier?.color} flex items-center justify-center`}>
                    <Icon 
                      name={tier?.id === 'free' ? 'User' : tier?.id === 'premium' ? 'Crown' : 'Building'} 
                      size={32} 
                      color="white" 
                    />
                  </div>
                  
                  <h3 className="text-xl font-bold text-foreground mb-2">{tier?.name}</h3>
                  
                  <div className="mb-3">
                    <span className="text-3xl font-bold text-foreground">{tier?.price}</span>
                    {tier?.period !== 'Forever' && (
                      <span className="text-muted-foreground ml-1">/{tier?.period?.split(' ')?.[1]}</span>
                    )}
                  </div>
                  
                  <p className="text-sm text-muted-foreground">{tier?.description}</p>
                </div>

                <div className="space-y-3 mb-6">
                  {tier?.features?.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                  
                  {tier?.limitations?.map((limitation, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Icon name="X" size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{limitation}</span>
                    </div>
                  ))}
                </div>

                <Button
                  variant={tier?.popular ? "default" : "outline"}
                  fullWidth
                  onClick={() => onSelectTier(tier?.id)}
                  className="premium-hover"
                >
                  {tier?.id === 'free' ? 'Start Free' : `Choose ${tier?.name}`}
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              All plans include 30-day money-back guarantee • Cancel anytime
            </p>
            <div className="flex items-center justify-center space-x-6 text-xs text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} className="text-success" />
                <span>Secure Payment</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Lock" size={16} className="text-success" />
                <span>Data Protected</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Headphones" size={16} className="text-success" />
                <span>24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipTierModal;